import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { useAppContext } from '../../context/AppContext'

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function extractTextFromChildren(children: React.ReactNode): string {
  let text = '';
  React.Children.forEach(children, child => {
    if (child == null) return;
    if (typeof child === 'string' || typeof child === 'number') {
      text += `${child} `;
    } else if (React.isValidElement(child)) {
      text += extractTextFromChildren((child.props as any).children);
    }
  });
  return text.trim().toLowerCase();
}

function Badge({ className, variant, children, ...props }: BadgeProps & { children?: React.ReactNode }) {
  const { userRole } = useAppContext();

  // Hide badges that show event/bounty state (e.g. 'active', 'published') unless the user is a sponsor (host).
  // This central rule ensures the UI doesn't reveal status labels to participants by default.
  try {
    const text = extractTextFromChildren(children);
    const isStateBadge = text.includes('active') || text.includes('published');
    if (isStateBadge && userRole !== 'sponsor') {
      return null;
    }
  } catch (e) {
    // swallow errors and render normally
  }

  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>{children}</div>
  )
}

export { Badge, badgeVariants }
